import { ButtonPrimary } from '@/components/button'
import { clearAuthCookies } from '@/utils/authCookies'
import { ROUTE_LOGIN } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { resetAuth } from '@/pages/auth/authSlice'
import { resetClub } from '@/pages/club/clubSlice'
import { resetEvent } from '@/pages/event/eventSlice'
import { resetPlayer } from '@/pages/player/playerSlice'
import { userSelector } from '@/pages/user/selectors'
import { resetUser } from '@/pages/user/userSlice'
import { queryClient } from '@/queryClient'
import { logout } from '@/services'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

type Props = {
  open: boolean
  onClose: () => void
}

export const LogoutModal = ({ open, onClose }: Props) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { chatClient } = useAppSelector(userSelector)
  const { t } = useTranslation()

  const handleLogout = () => {
    clearAuthCookies()
    logout()
    chatClient?.disconnectUser()
    dispatch(resetAuth())
    dispatch(resetUser())
    dispatch(resetEvent())
    dispatch(resetPlayer())
    dispatch(resetClub())
    queryClient.clear()
    navigate(ROUTE_LOGIN)
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col max-w-2xl transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('common.verifyEmail')}
                </Dialog.Title>
                <div className="flex flex-col py-4 gap-y-6">
                  <p className="text-xs text-green-500">A link was sent to your new email!</p>
                  <p className="text-sm text-gray-300">
                    Now, you will be logged out, and you can again login with the new email provided.
                  </p>
                </div>
                <div className="flex  justify-center gap-x-4">
                  <ButtonPrimary title="Logout" className={`!py-[6px] !px-4 !rounded`} onClick={handleLogout} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
